import z from "zod";

import { type ErrorDetails, RemiHttpCode } from "./remi-app";

const jobSchedulerErrorTypeValues = [
  "PLACEHOLDER_ERROR",
  "OPENPHONE_SMS_VIBE_CHECK_FAILED",
  "OPENPHONE_PHONE_CALL_VIBE_CHECK_FAILED",
  "DOCUMENT_EXPIRATION_REMINDER_BATCH_FAILED",
  "TICKET_TEAM_RECONCILED",
  "TASK_TEAM_RECONCILED",
  "CONVERSATION_ASSIGNEE_RECONCILE_CAP_EXCEEDED",
  "TASK_CONVERSATION_ASSIGNMENT_RECONCILED",
  "TASK_CONVERSATION_ASSIGNMENT_RECONCILE_FAILED",
  "OWNERSHIP_RECONCILE_TEAM_UNASSIGNABLE",
  "OWNERSHIP_RECONCILE_REPORT_UNBALANCED",
  "PHONE_PLACEMENT_CAP_TRIPPED",
  "PHONE_PLACEMENT_TEAM_NOT_ASSIGNABLE",
  "OPENPHONE_TRANSCRIPT_RECOVERY_ALL_FAILED",
  "ESCALATION_SEAT_CAP_TRIPPED",
] as const;

const jobSchedulerErrorTypeSchema = z.enum(jobSchedulerErrorTypeValues);
const JobSchedulerErrorType = jobSchedulerErrorTypeSchema.enum;
type JobSchedulerErrorType = z.infer<typeof jobSchedulerErrorTypeSchema>;

export type JobSchedulerErrors =
  | {
      errorType: typeof JobSchedulerErrorType.PLACEHOLDER_ERROR;
      data: Record<string, never>;
    }
  | {
      errorType: typeof JobSchedulerErrorType.OPENPHONE_SMS_VIBE_CHECK_FAILED;
      data: { lastRecordAt: Date | null; thresholdMinutes: number; weekendThresholdMinutes: number };
    }
  | {
      errorType: typeof JobSchedulerErrorType.OPENPHONE_PHONE_CALL_VIBE_CHECK_FAILED;
      data: { lastRecordAt: Date | null; thresholdMinutes: number; weekendThresholdMinutes: number };
    }
  | {
      errorType: typeof JobSchedulerErrorType.DOCUMENT_EXPIRATION_REMINDER_BATCH_FAILED;
      data: { totalContractors: number; failedCount: number; failedContractorProfileIds: string[] };
    }
  | {
      errorType: typeof JobSchedulerErrorType.TICKET_TEAM_RECONCILED;
      data: { ticketId: string; bidId: number; previousTeamId: string; correctedTeamId: string };
    }
  | {
      errorType: typeof JobSchedulerErrorType.TASK_TEAM_RECONCILED;
      data: { taskId: string; projectId: number; previousTeamId: string; correctedTeamId: string };
    }
  | {
      errorType: typeof JobSchedulerErrorType.CONVERSATION_ASSIGNEE_RECONCILE_CAP_EXCEEDED;
      data: {
        abortReason: "WRITE_CAP" | "WORKSITE_GROUP_CAP";
        writes: number;
        worksiteGroupsProcessed: number;
        worksiteGroupsRemaining: number;
      };
    }
  | {
      errorType: typeof JobSchedulerErrorType.TASK_CONVERSATION_ASSIGNMENT_RECONCILED;
      data: {
        conversationId: string;
        taskId: string;
        previousTeamId: string | null;
        correctedTeamId: string;
        previousAssigneeId: string | null;
        correctedAssigneeId: string | null;
      };
    }
  | {
      errorType: typeof JobSchedulerErrorType.TASK_CONVERSATION_ASSIGNMENT_RECONCILE_FAILED;
      data: { failed: number; scanned: number };
    }
  | {
      errorType: typeof JobSchedulerErrorType.OWNERSHIP_RECONCILE_TEAM_UNASSIGNABLE;
      data: { cause: string; teamId: string | null; teamKey: string | null };
    }
  | {
      errorType: typeof JobSchedulerErrorType.PHONE_PLACEMENT_CAP_TRIPPED;
      data: {
        candidates: number;
        proposedMoves: number;
        moveCap: number;
        byFeed: Record<string, number>;
      };
    }
  | {
      errorType: typeof JobSchedulerErrorType.ESCALATION_SEAT_CAP_TRIPPED;
      data: {
        worksitesSelected: number;
        fanoutCap: number;
      };
    }
  | {
      errorType: typeof JobSchedulerErrorType.PHONE_PLACEMENT_TEAM_NOT_ASSIGNABLE;
      data: {
        notAssignableCount: number;
        sample: { conversationId: string; teamId: string | null }[];
      };
    }
  | {
      errorType: typeof JobSchedulerErrorType.OWNERSHIP_RECONCILE_REPORT_UNBALANCED;
      data: {
        scannedCount: number;
        outOfScopeNoLiveBidCount: number;
        appliedCount: number;
        casConflictCount: number;
        skippedPinnedCount: number;
        skippedNoOpCount: number;
        skippedNoDrivingBidCount: number;
        skippedFlagOffCount: number;
        skippedNotFoundCount: number;
        erroredCount: number;
      };
    }
  | {
      errorType: typeof JobSchedulerErrorType.OPENPHONE_TRANSCRIPT_RECOVERY_ALL_FAILED;
      data: {
        candidates: number;
        recovered: number;
        quoUnavailable: number;
        errored: number;
        deferred: number;
        stoppedEarly: boolean;
      };
    };

export const jobSchedulerErrorDetails: Record<JobSchedulerErrorType, ErrorDetails> = {
  [JobSchedulerErrorType.PLACEHOLDER_ERROR]: {
    sentryDescription: "Job scheduler error",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [JobSchedulerErrorType.OPENPHONE_SMS_VIBE_CHECK_FAILED]: {
    sentryDescription: "No new OpenPhone SMS messages within lookback window",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [JobSchedulerErrorType.OPENPHONE_PHONE_CALL_VIBE_CHECK_FAILED]: {
    sentryDescription: "No new OpenPhone phone calls within lookback window",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [JobSchedulerErrorType.DOCUMENT_EXPIRATION_REMINDER_BATCH_FAILED]: {
    sentryDescription: "Document expiration reminder batch had failures",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [JobSchedulerErrorType.TICKET_TEAM_RECONCILED]: {
    sentryDescription: "Open ticket was reteamed by nightly reconciliation; it was created with a stale team",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [JobSchedulerErrorType.TASK_TEAM_RECONCILED]: {
    sentryDescription: "Open task was reteamed by nightly reconciliation; it was created with a stale team",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [JobSchedulerErrorType.CONVERSATION_ASSIGNEE_RECONCILE_CAP_EXCEEDED]: {
    sentryDescription:
      "Conversation-assignee reconciliation hit a safety cap and left worksite groups unprocessed; the next run's lookback window re-covers them, but a wave large enough to trip the cap warrants a human look",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [JobSchedulerErrorType.TASK_CONVERSATION_ASSIGNMENT_RECONCILED]: {
    sentryDescription:
      "Contractor task conversation was reassigned by nightly reconciliation; it was left on routing its task no longer resolves to",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [JobSchedulerErrorType.TASK_CONVERSATION_ASSIGNMENT_RECONCILE_FAILED]: {
    sentryDescription:
      "Nightly task conversation reconciliation could not process some task threads; they keep routing to a stale team or assignee until a run succeeds",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [JobSchedulerErrorType.OWNERSHIP_RECONCILE_TEAM_UNASSIGNABLE]: {
    sentryDescription: "A team the ownership resolver can emit is not assignable to conversations",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [JobSchedulerErrorType.PHONE_PLACEMENT_CAP_TRIPPED]: {
    sentryDescription:
      "The phone placement reconciler proposed more moves than its cap allows, so it wrote nothing; a wave this large is a routing change or a bulk script, and wants a human look before the cap is raised",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [JobSchedulerErrorType.ESCALATION_SEAT_CAP_TRIPPED]: {
    sentryDescription:
      "The daily escalation seat fan-out selected more worksites than its cap; every one was still requested, but a selection this large means escalations or seat markers are piling up and wants a human look",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [JobSchedulerErrorType.PHONE_PLACEMENT_TEAM_NOT_ASSIGNABLE]: {
    sentryDescription:
      "The phone placement reconciler decided a team that is deleted, cannot be assigned conversations, or has no live members, so those threads were left where they are",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [JobSchedulerErrorType.OWNERSHIP_RECONCILE_REPORT_UNBALANCED]: {
    sentryDescription:
      "The ownership sweep report's denominator identity failed, so a conversation outcome was counted twice or not at all",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [JobSchedulerErrorType.OPENPHONE_TRANSCRIPT_RECOVERY_ALL_FAILED]: {
    sentryDescription:
      "Every candidate in the OpenPhone transcript recovery sweep errored, so recorded calls missing a transcript were not recovered from Quo",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
};
